// hooks/useCompanionShipmentsHandoff.ts
//
// Resolves the "See all shipments" handoff from the Home companion panel.
// Everything arrives via router `location.state` — no Redux involved here
// on purpose, since Redux is going away; the caller (NewShipments) applies
// the resolved tab id through whatever tab-state mechanism it currently
// uses.
//
// Rules:
//   - Default view "active"          -> resolve to the 'active' tab directly.
//   - Default view anything else
//     (e.g. "open_alerts")           -> resolve to the 'all_shipments' tab.
//   - Shared / personal saved filter -> activate the matching pinned tab if
//     one already exists; otherwise pin it first, then activate the result.
//
// `isResolving` stays true from mount until a decision is reached, so the
// caller can hold off its initial v3 fetch until the right tab id is known.

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { pinFilterSetAsTab } from '@/apis/shipmentTabs';
import type {
  CompanionHandoffIntent,
  ShipmentTab,
  UseCompanionShipmentsHandoffParams,
  UseCompanionShipmentsHandoffResult,
} from '../types';

const HANDOFF_STATE_KEY = '__zl_companion_handoff';

export const useCompanionShipmentsHandoff = ({
  token,
  customTabs,
  tabsLoaded,
}: UseCompanionShipmentsHandoffParams): UseCompanionShipmentsHandoffResult => {
  const location = useLocation();
  const navigate = useNavigate();

  // Captured once on mount — location.state is stripped right after so
  // back-navigation / remounts don't reapply it.
  const intentRef = useRef<CompanionHandoffIntent | undefined>(
    (location.state as Record<string, unknown> | undefined)?.[
      HANDOFF_STATE_KEY
    ] as CompanionHandoffIntent | undefined
  );
  const didResolveRef = useRef(false);

  const [resolvedTabId, setResolvedTabId] = useState<string | null>(null);
  const [newlyPinnedTab, setNewlyPinnedTab] = useState<ShipmentTab | null>(
    null
  );
  const [isResolving, setIsResolving] = useState(Boolean(intentRef.current));

  useEffect(() => {
    if (!intentRef.current) return;

    const state = (location.state ?? {}) as Record<string, unknown>;
    if (!(HANDOFF_STATE_KEY in state)) return;

    const { [HANDOFF_STATE_KEY]: _ignore, ...rest } = state;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: rest }
    );
    // One-shot on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const intent = intentRef.current;
    if (!intent || didResolveRef.current) return;

    if (intent.kind === 'default' && intent.id === 'active') {
      didResolveRef.current = true;
      setResolvedTabId('active');
      setIsResolving(false);
      return;
    }

    if (intent.kind === 'default') {
      // e.g. "open_alerts" — not a pinnable tab itself, land on All shipments.
      didResolveRef.current = true;
      setResolvedTabId('all_shipments');
      setIsResolving(false);
      return;
    }

    // Shared / personal — need the current tabs list to check for an
    // existing pin before deciding whether to create one.
    if (!tabsLoaded) return;

    didResolveRef.current = true;

    const existingTab = customTabs.find(
      (tab) => tab.savedFilterSet?.id === intent.id
    );

    if (existingTab) {
      setResolvedTabId(existingTab.id);
      setIsResolving(false);
      return;
    }

    (async () => {
      try {
        const tab = await pinFilterSetAsTab(token, intent.id);
        setNewlyPinnedTab(tab);
        setResolvedTabId(tab.id);
      } finally {
        setIsResolving(false);
      }
    })();
  }, [tabsLoaded, customTabs, token]);

  return {
    isResolving,
    resolvedTabId,
    resolvedSortKey: intentRef.current?.sortKey,
    resolvedSortOrder: intentRef.current?.sortOrder,
    newlyPinnedTab,
  };
};

export { HANDOFF_STATE_KEY as COMPANION_HANDOFF_STATE_KEY };
